import styles from '../components/organisms/DoctorAddMedicineToOrderForm.module.css';

import classNames from 'classnames';
import { format } from 'date-fns';

import PlusIcon from '../assets/icons/plus-white.svg?react';
import TrashIcon from '../assets/icons/trash.svg?react';
import SuccessIcon from '../assets/images/success.svg?react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Link,
  generatePath,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';

import { CommonApi, OrderApi, PeopleApi, ReceiptApi } from '../api';
import WarningIcon from '../assets/icons/warning.png';
import {
  AddressAutocomplete,
  CheckMessagesBlock,
  CreateAddressModal,
  DoctorAddMedicineToOrderForm,
  OrderMedicine,
  PatientAutocomplete,
  PrivateRoute,
} from '../components';
import { Modal } from '../components/molecules/Modal';
import { RoutePath } from '../config';
import { useProfile } from '../hooks';
import { DoctorInnerPageLayout } from '../layouts';
import { AddressResponse, PickupAddressListResponse } from '../model/Address';
import { formatMedicinePlural, formatMoney } from '../utils';

interface FormFields {
  description: string;
  patientId: number;
  address: AddressResponse;
  flat: string;
}

/** Временно скрыты выбор доставки и адрес на странице создания заказа доктором */
const SHOW_DELIVERY_OPTIONS_ON_DOCTOR_ORDER = false;

const CameraIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.5 19.5H4.5C4.10218 19.5 3.72064 19.342 3.43934 19.0607C3.15804 18.7794 3 18.3978 3 18V7.5C3 7.10218 3.15804 6.72064 3.43934 6.43934C3.72064 6.15804 4.10218 6 4.5 6H7.5L9 3.75H15L16.5 6H19.5C19.8978 6 20.2794 6.15804 20.5607 6.43934C20.842 6.72064 21 7.10218 21 7.5V18C21 18.3978 20.842 18.7794 20.5607 19.0607C20.2794 19.342 19.8978 19.5 19.5 19.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.75C13.864 15.75 15.375 14.239 15.375 12.375C15.375 10.511 13.864 9 12 9C10.136 9 8.625 10.511 8.625 12.375C8.625 14.239 10.136 15.75 12 15.75Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FileIcon = ({ className = '' }: { className?: string }) => (
  <span className="flex size-[36px] items-center justify-center rounded-full bg-[#e4eff8] p-[8px]">
    <svg
      className={classNames(className, 'relative left-[1px]')}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.625 17.5H4.375C4.20924 17.5 4.05027 17.4342 3.93306 17.3169C3.81585 17.1997 3.75 17.0408 3.75 16.875V3.125C3.75 2.95924 3.81585 2.80027 3.93306 2.68306C4.05027 2.56585 4.20924 2.5 4.375 2.5H11.875L16.25 6.875V16.875C16.25 17.0408 16.1842 17.1997 16.0669 17.3169C15.9497 17.4342 15.7908 17.5 15.625 17.5Z"
        stroke="#0788F1"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.875 2.5V6.875H16.25"
        stroke="#0788F1"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const TrashIconSmall = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.5">
      <path
        d="M16.875 4.375H3.125"
        stroke="#151515"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.125 8.125V13.125"
        stroke="#151515"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.875 8.125V13.125"
        stroke="#151515"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.625 4.375V16.25C15.625 16.4158 15.5592 16.5747 15.4419 16.6919C15.3247 16.8092 15.1658 16.875 15 16.875H5C4.83424 16.875 4.67527 16.8092 4.55806 16.6919C4.44085 16.5747 4.375 16.4158 4.375 16.25V4.375"
        stroke="#151515"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.125 4.375V3.125C13.125 2.79348 12.9933 2.47554 12.7589 2.24112C12.5245 2.0067 12.2065 1.875 11.875 1.875H8.125C7.79348 1.875 7.47554 2.0067 7.24112 2.24112C7.0067 2.47554 6.875 2.79348 6.875 3.125V4.375"
        stroke="#151515"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

type UploadedFileItemProps = {
  fileName: string;
  onDelete: () => void;
};

const UploadedFileItem = ({ fileName, onDelete }: UploadedFileItemProps) => (
  <div className="flex w-full items-center gap-[12px] rounded-md border border-[#e5e7eb] bg-white px-[16px] py-[12px]">
    <div className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-[#e4f0f7]">
      <FileIcon />
    </div>
    <div className="min-w-0 flex-1 truncate text-[14px] font-normal leading-5 text-[#151515]">
      {fileName}
    </div>
    <button
      type="button"
      onClick={onDelete}
      className="flex size-[36px] shrink-0 items-center justify-center rounded-full text-[#151515]/50 transition-colors hover:bg-[#f1f5f9] hover:text-[#151515]"
      aria-label="Удалить файл"
    >
      <TrashIconSmall className="size-[20px]" />
    </button>
  </div>
);

export const DoctorCreateOrderPage = () => {
  const profile = useProfile();
  const flow = profile?.flow || 'form_only';

  const [isCheckSuccessful, setIsCheckSuccessful] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [prescriptionFiles, setPrescriptionFiles] = useState<
    { id: number; fileName: string }[]
  >([]);
  const pendingFileNameRef = useRef<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [patientAddressId, setPatientAddressId] = useState<
    number | undefined
  >();

  const [checkMessages, setCheckMessages] = useState<
    { message: string; level: 'info' | 'warning' | 'danger' }[]
  >([]);

  const { mutate: checkMutate, isLoading: isChecking } = useMutation({
    mutationFn: ReceiptApi.check,
    onSuccess(response) {
      setCheckMessages(response);
      setIsCheckSuccessful(true);
    },
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPatientIdParam = searchParams.get('patientId');
  const preselectedPatientId =
    preselectedPatientIdParam && /^\d+$/.test(preselectedPatientIdParam)
      ? Number(preselectedPatientIdParam)
      : 0;
  const patientFieldOutlineAttention = preselectedPatientId > 0;
  const [showOrderSuccessSent, setShowOrderSuccessSent] = useState(false);
  const fileOnlyConfirmRef = useRef(false);
  const showSuccessModalRef = useRef(false);
  const successPatientIdRef = useRef<number | undefined>(undefined);
  const formAddressIdRef = useRef<number | undefined>(undefined);
  const flowRef = useRef(flow);
  const patientAddressIdRef = useRef<number | undefined>(undefined);
  flowRef.current = flow;
  patientAddressIdRef.current = patientAddressId;

  const { mutate: confirmOrder } = useMutation({
    mutationFn: (params: {
      receiptId: number;
      addressId: number;
      receiptType?: 'receipt' | 'free_form_receipt';
    }) =>
      OrderApi.confirm(
        params.receiptId,
        params.addressId,
        params.receiptType ?? 'receipt',
      ),
    onSuccess: (data) => {
      const patientId = data?.data?.patient_id;
      successPatientIdRef.current = patientId;
      if (fileOnlyConfirmRef.current) {
        fileOnlyConfirmRef.current = false;
        setShowOrderSuccessSent(true);
      } else if (showSuccessModalRef.current) {
        showSuccessModalRef.current = false;
        setShowOrderSuccessSent(true);
      } else {
        if (patientId) {
          navigate(
            generatePath(RoutePath.DoctorPatientDetails, {
              patientId: String(patientId),
            }),
          );
        }
      }
    },
  });
  const { mutate: creationMutate, isLoading: isCreating } = useMutation({
    mutationFn: ReceiptApi.create,
    onSuccess(response) {
      if (flowRef.current === 'file_only') {
        fileOnlyConfirmRef.current = true;
        confirmOrder({
          receiptId: response.receipt_id,
          addressId: formAddressIdRef.current ?? 0,
        });
      } else {
        showSuccessModalRef.current = true;
        confirmOrder({
          receiptId: response.receipt_id,
          addressId: formAddressIdRef.current ?? 0,
          receiptType: 'receipt',
        });
      }
    },
  });

  const { mutate: uploadDocument } = useMutation({
    mutationFn: (formData: FormData) => ReceiptApi.uploadDocument(formData),
    onSuccess: (response) => {
      const documentId = response?.data?.document_id;
      if (documentId) {
        const fileName = pendingFileNameRef.current ?? 'Файл';
        pendingFileNameRef.current = null;
        setPrescriptionFiles((prev) => [...prev, { id: documentId, fileName }]);
        setIsUploading(false);
        setUploadError(null);
      } else {
        setUploadError('Не удалось получить ID загруженного файла');
        setIsUploading(false);
      }
    },
    onError: () => {
      setUploadError('Ошибка загрузки файла');
      setIsUploading(false);
    },
  });

  const [medicineList, setMedicineList] = useState<OrderMedicine[]>([]);
  const [deliveryType, setDeliveryType] = useState<string>('delivery');

  const { data: paymentList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 206],
    queryFn: () => CommonApi.getReference(206),
  });

  useQuery({
    queryKey: [CommonApi.KEY, 'getRefHeaders'],
    queryFn: CommonApi.getRefHeaders,
  });

  const { control, getValues, reset, trigger, watch } = useForm<FormFields>({
    defaultValues: {
      description: '',
      flat: '',
      patientId: preselectedPatientId,
    },
  });

  const patientId = watch('patientId');

  // Получаем данные выбранного пациента
  const { data: selectedPatient } = useQuery({
    queryKey: [PeopleApi.KEY, 'getPatientDetails', patientId],
    queryFn: () => PeopleApi.getPatientDetails(String(patientId!)),
    enabled: flow === 'file_only' && !!patientId,
  });

  const closeConfirmOrderModal = useCallback(() => {
    reset();
    setPrescriptionFiles([]);
    setMedicineList([]);
  }, [reset]);

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setUploadError(null);
      pendingFileNameRef.current = file.name;
      setIsUploading(true);

      const formData = new FormData();
      formData.append('document', file);
      if (patientId) {
        formData.append('patient_id', patientId.toString());
      }

      uploadDocument(formData);
    },
    [uploadDocument, patientId],
  );

  const handleFileUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const checkOrder = useCallback(async () => {
    const [description] = getValues(['description']);
    checkMutate({
      description: (description ?? '').trim(),
      receipt_items: medicineList.flatMap((medicine) =>
        medicine.time.map((time) => ({
          total_amount: medicine.totalCount,
          amount: Number(time.count),
          duration: medicine.duration,
          start: medicine.start,
          daytime_name: time.value,
          frequency_name: medicine.frequency.code,
          eating_name: time.value,
          medicine_name: medicine.medicine.name,
        })),
      ),
    });
  }, [getValues, medicineList, checkMutate]);

  const createOrder = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    const [description, patientId, address] = getValues([
      'description',
      'patientId',
      'address',
    ]);
    formAddressIdRef.current = address?.id;
    const paymentMethodId =
      paymentList.find((p) => /юкасса|yookassa/i.test(p.value))?.id ??
      paymentList[0]?.id;
    if (!paymentMethodId) {
      setUploadError('Не удалось получить способ оплаты');
      return;
    }
    // Опциональный комментарий → Receipt.description → Order (бэкенд create_order).
    creationMutate({
      description: (description ?? '').trim(),
      patient_id: patientId,
      address: address.id,
      payment_method: paymentMethodId,
      analysis_result: checkMessages,
      recipient: {
        surname: '',
        name: '',
      },
      receipt_items: medicineList.flatMap((medicine) =>
        medicine.time.map((time) => ({
          total_amount: medicine.totalCount,
          amount: Number(time.count),
          duration: medicine.duration,
          start: medicine.start,
          daytime_id: time.timeId,
          frequency_id: medicine.frequency.id,
          eating_id: time.eatingId,
          medicine_id: medicine.medicine.id,
        })),
      ),
      ...(prescriptionFiles.length > 0 && {
        scanned_receipts: prescriptionFiles.map((f) => f.id),
      }),
    });
  }, [
    getValues,
    medicineList,
    creationMutate,
    trigger,
    checkMessages,
    prescriptionFiles,
    paymentList,
  ]);

  const createOrderWithFile = useCallback(async () => {
    const isValid = await trigger(['patientId']);
    if (!isValid) {
      return;
    }
    const [patientId, description] = getValues(['patientId', 'description']);
    let addressId = 0;

    if (flow === 'file_only') {
      if (deliveryType === 'delivery') {
        const address = getValues('address');
        const flat = getValues('flat');
        if (address?.address_text && flat) {
          const addresResult = {
            address_text: address?.address_text,
            people_main_id: patientId,
            flat: flat,
          };
          const createdAddress = await PeopleApi.createPatientsAddress(
            addresResult as AddressResponse,
          );
          addressId = createdAddress?.data?.id || 0;
        }
      } else {
        addressId = pickupAddressesData?.items?.[0]?.id || 0;
      }
    } else {
      addressId = patientAddressId || 0;
    }

    if (!prescriptionFiles.length) {
      setUploadError('Необходимо загрузить файл с назначением');
      return;
    }

    if (!selectedPatient) {
      setUploadError('Не удалось загрузить данные пациента');
      return;
    }

    // По умолчанию Юкасса
    const paymentMethodId =
      paymentList.find((p) => /юкасса|yookassa/i.test(p.value))?.id ??
      paymentList[0]?.id;
    if (!paymentMethodId) {
      setUploadError('Не удалось получить способ оплаты');
      return;
    }

    // Формируем recipient из данных выбранного пациента
    const recipient = {
      surname: selectedPatient.surname || '',
      name: selectedPatient.name || '',
      midname: selectedPatient.midname,
      birthdate:
        selectedPatient.birthdate &&
        selectedPatient.birthdate instanceof Date &&
        !isNaN(selectedPatient.birthdate.getTime())
          ? format(selectedPatient.birthdate, 'dd-MM-yyyy')
          : undefined,
      email: selectedPatient.email,
    };

    formAddressIdRef.current = addressId;

    // Не подставляем имена файлов в description — иначе они станут
    // комментарием заказа. Пустая строка = без комментария (как на бэке).
    creationMutate({
      patient_id: patientId,
      address: addressId,
      description: (description ?? '').trim(),
      payment_method: paymentMethodId,
      analysis_result: [],
      scanned_receipts: prescriptionFiles.map((f) => f.id),
      recipient,
      receipt_items: [], // Для file_only flow нет лекарств
    });
  }, [
    getValues,
    trigger,
    prescriptionFiles,
    selectedPatient,
    patientAddressId,
    paymentList,
    creationMutate,
  ]);

  // Получаем адрес пациента автоматически для file_only flow
  const { data: patientAddressesData } = useQuery({
    queryKey: [PeopleApi.KEY, 'getAddress', patientId],
    queryFn: () => PeopleApi.getAddress(patientId),
    enabled: flow === 'file_only' && !!patientId,
  });

  const { data: pickupAddressesData } = useQuery<PickupAddressListResponse>({
    queryKey: [PeopleApi.KEY, 'getPickupAddress'],
    queryFn: () => PeopleApi.getPickupAddress(),
  });

  useEffect(() => {
    if (patientAddressesData?.items?.[0]?.id) {
      setPatientAddressId(patientAddressesData.items[0].id);
    } else {
      setPatientAddressId(undefined);
    }
  }, [patientAddressesData]);

  useEffect(() => {
    setIsCheckSuccessful(false);
  }, [medicineList]);

  const handleOrderSuccessClose = useCallback(() => {
    setShowOrderSuccessSent(false);
    closeConfirmOrderModal();
    const pid = successPatientIdRef.current;
    successPatientIdRef.current = undefined;
    if (pid) {
      navigate(
        generatePath(RoutePath.DoctorPatientDetails, {
          patientId: String(pid),
        }),
      );
    }
  }, [closeConfirmOrderModal, navigate]);

  // Форма только с файлом (file_only)
  if (flow === 'file_only') {
    return (
      <PrivateRoute redirectPath={RoutePath.DoctorLogin}>
        <DoctorInnerPageLayout>
          <Modal
            isOpen={showOrderSuccessSent}
            onClose={handleOrderSuccessClose}
          >
            <div className="p-[72px] text-center">
              <span className="flex justify-center">
                <SuccessIcon />
              </span>
              <h2 className="mt-[24px] text-[24px] leading-none">
                Ваш заказ успешно отправлен
              </h2>
            </div>
          </Modal>
          <div className="grid grid-cols-[1fr_1.5fr] gap-[4px]">
            <div className="rounded-l-lg bg-white text-brand-1">
              <header className="rounded-tl-lg bg-brand-6 px-[24px] py-[16px]">
                <h2 className="text-[20px] font-bold leading-normal">Детали</h2>
              </header>
              <div className="flex flex-col gap-[32px] border-b border-dashed border-brand-1/50 p-[24px]">
                <div className="text-[14px] leading-[20px] text-[#151515]">
                  Выберите или добавьте пациента, которому хотите назначить
                  лекарственный препарат{' '}
                </div>
                <Controller
                  control={control}
                  name="patientId"
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => (
                    <PatientAutocomplete
                      className="flex-1"
                      outlineAttention={patientFieldOutlineAttention}
                      placeholder="Найти пациента"
                      error={fieldState.error}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  )}
                />
              </div>
              <div className="flex flex-col gap-[18px] border-b border-dashed border-brand-1/50 p-[24px]">
                <Link
                  className="button button--primary px-[12px]"
                  to={RoutePath.DoctorCreatePatient}
                >
                  <span className="relative flex items-center justify-center gap-[10px]">
                    <div className="absolute left-1/2 top-[-57px] -translate-x-1/2 bg-white px-[15px] text-[16px] text-brand-1/50">
                      или
                    </div>
                    <PlusIcon /> Добавить пациента
                  </span>
                </Link>
              </div>
              <div className="flex flex-col gap-[18px] border-b border-dashed border-brand-1/50 p-[24px]">
                <div className="text-[14px] leading-[20px] text-[#151515]">
                  Загрузите фото рецепта или назначения для выбранного выше
                  пациента
                </div>
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-center gap-[12px] rounded-md border-2 border-dashed border-[#0788f1]/50 bg-[#e4f0f7] py-[24px] transition-colors hover:border-[#0788f1] hover:bg-[#d4e8f2]"
                  onClick={handleFileUploadClick}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <svg
                      className="size-[32px] shrink-0 animate-spin text-[#0788f1]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <CameraIcon className="size-6 shrink-0 text-[#0788f1]" />
                  )}
                  <span className="text-[14px] font-medium leading-5 text-[#0788f1]">
                    {isUploading
                      ? 'Загрузка...'
                      : 'Загрузите файл с назначением'}
                  </span>
                </button>
                {prescriptionFiles.length > 0 && (
                  <div className="flex flex-col gap-[8px]">
                    {prescriptionFiles.map((file) => (
                      <UploadedFileItem
                        key={file.id}
                        fileName={file.fileName}
                        onDelete={() => {
                          setUploadError(null);
                          setPrescriptionFiles((prev) =>
                            prev.filter((f) => f.id !== file.id),
                          );
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
                {uploadError && (
                  <div className="text-red-500 mt-[12px] text-sm">
                    {uploadError}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              <div className="flex flex-col gap-[16px] p-[30px]">
                {SHOW_DELIVERY_OPTIONS_ON_DOCTOR_ORDER && (
                  <>
                    <div className="text-[14px] leading-[20px] text-[#151515]">
                      Выберите тип доставки и укажите адрес
                    </div>
                    <div className="mb-[9px] flex gap-[12px]">
                      {[
                        { title: 'Доставка', value: 'delivery' },
                        { title: 'Самовывоз', value: 'pickup' },
                      ].map((item) => (
                        <label className={styles.radio} key={item.value}>
                          <input
                            className="hidden"
                            type="checkbox"
                            checked={item.value === deliveryType}
                            onChange={() => setDeliveryType(item.value)}
                          />
                          <span className="whitespace-nowrap leading-normal">
                            {item.title}
                          </span>
                        </label>
                      ))}
                    </div>
                    {deliveryType === 'delivery' && (
                      <div className="flex gap-[12px]">
                        <Controller
                          control={control}
                          name="address"
                          render={({ field }) => (
                            <AddressAutocomplete
                              value={field.value}
                              onChange={(address) => {
                                field.onChange(address);
                              }}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="flat"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              placeholder="Квартира"
                              className="input max-w-[120px]"
                            />
                          )}
                        />
                      </div>
                    )}
                    {deliveryType === 'pickup' && (
                      <div className="flex flex-col gap-[16px]">
                        <div className="flex flex-col gap-[6px]">
                          <div className="text-[14px] leading-[20px] text-brand-1/50">
                            Адрес самовывоза:
                          </div>
                          <div className="text-[14px] leading-[20px] text-[#151515]">
                            {pickupAddressesData?.items?.[0]?.address_text ||
                              ''}
                          </div>
                        </div>
                        <div className="flex flex-col gap-[6px]">
                          <div className="text-[14px] leading-[20px] text-brand-1/50">
                            Время работы:
                          </div>
                          <div className="text-[14px] leading-[20px] text-[#151515]">
                            Пн-Пт: 10:00 до 20:00, Сб-Вс: выходной
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="flex gap-[10px] rounded-[10px] bg-[#FDF4E1] p-[12px] text-[16px] leading-[20px] text-[#151515]">
                  <img
                    src={WarningIcon}
                    alt="Внимание"
                    width={20}
                    height={20}
                    className="max-h-[20px]"
                  />
                  В ближайшее время мы свяжемся с пациентом.
                </div>
              </div>
            </div>
            <div className="flex flex-col overflow-hidden rounded-r-lg bg-white text-brand-1">
              <div className="grow">
                <header className="rounded-tr-lg bg-brand-6 px-[24px] py-[16px]">
                  <h2 className="text-[20px] font-bold leading-normal">
                    Заказ
                  </h2>
                </header>
                <div className="border-b-4 border-brand-3 px-[24px] pb-[24px] pt-[12px]">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="w-[52px]">&nbsp;</th>
                        <th className="text-left">Лекарство</th>
                        <th className="text-left">Частота приёма</th>
                        <th className="text-left">Цена</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-center" colSpan={4}>
                          Пусто
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Controller
                  control={control}
                  name="description"
                  render={({ field, fieldState }) => (
                    <label className="block w-full p-[24px]">
                      <span className="mb-[12px] block">
                        Комментарий к заказу
                      </span>
                      <TextareaAutosize
                        {...field}
                        minRows={3}
                        maxRows={10}
                        className={classNames('input w-full resize-none', {
                          'input--invalid': fieldState.error,
                        })}
                        placeholder="Введите комментарий (необязательно)"
                      />
                    </label>
                  )}
                />
              </div>
              <footer className="flex justify-end border-t border-dashed border-brand-1/50 p-[24px]">
                <button
                  className="button button--success px-[32px]"
                  disabled={
                    !prescriptionFiles.length ||
                    !patientId ||
                    !selectedPatient ||
                    isCreating
                  }
                  onClick={createOrderWithFile}
                  type="button"
                >
                  {isCreating ? <>Загрузка...</> : 'Сформировать заказ'}
                </button>
              </footer>
            </div>
          </div>
        </DoctorInnerPageLayout>
      </PrivateRoute>
    );
  }

  // Форма только с лекарствами (form_only) или гибридная (hybrid)
  const showFileUpload = flow === 'hybrid';

  return (
    <PrivateRoute redirectPath={RoutePath.DoctorLogin}>
      <DoctorInnerPageLayout>
        <Modal isOpen={showOrderSuccessSent} onClose={handleOrderSuccessClose}>
          <div className="p-[72px] text-center">
            <span className="flex justify-center">
              <SuccessIcon />
            </span>
            <h2 className="mt-[24px] text-[24px] leading-none">
              Ваш заказ успешно отправлен
            </h2>
          </div>
        </Modal>
        <div className="grid grid-cols-[1fr_1.5fr] gap-[4px]">
          <div className="overflow-visible rounded-l-lg bg-white text-brand-1">
            <header className="rounded-tl-lg bg-brand-6 px-[24px] py-[16px]">
              <h2 className="text-[20px] font-bold leading-normal">Детали</h2>
            </header>
            <div className="flex gap-[12px] border-b border-dashed border-brand-1/50 p-[24px]">
              <Controller
                control={control}
                name="patientId"
                rules={{
                  required: true,
                }}
                render={({ field, fieldState }) => (
                  <PatientAutocomplete
                    className="flex-1"
                    outlineAttention={patientFieldOutlineAttention}
                    error={fieldState.error}
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
              <Link
                className="button button--primary px-[12px]"
                to={RoutePath.DoctorCreatePatient}
              >
                Добавить
              </Link>
            </div>
            <div className="flex gap-[12px] border-b border-dashed border-brand-1/50 p-[24px]">
              <Controller
                control={control}
                name="address"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <div className="mb-[12px] flex w-full items-center gap-[8px]">
                    <input
                      type="text"
                      readOnly
                      className={classNames(
                        'input',
                        !field.value
                          ? 'text-brand-1/40 border-brand-2'
                          : 'text-black',
                      )}
                      value={field.value?.address_text || 'Выберите адрес'}
                      onClick={() => setIsModalOpen(true)}
                    />
                    <CreateAddressModal
                      isOpen={isModalOpen}
                      onClose={() => setIsModalOpen(false)}
                      patientId={patientId}
                      initialData={field.value}
                      onAddressSelect={(address) => {
                        field.onChange(address);
                        setIsModalOpen(false);
                      }}
                    />
                  </div>
                )}
              />
            </div>
            {showFileUpload && (
              <>
                <div className="flex flex-col gap-[12px] border-b border-dashed border-brand-1/50 p-[24px]">
                  <div
                    className="flex w-full cursor-pointer items-center justify-center gap-[12px] rounded-[12px] border border-[#0ea5e880] bg-[#0ea5e91a] p-[24px] outline outline-[.9] outline-offset-[-1px] outline-[#0ea5e880]"
                    onClick={handleFileUploadClick}
                  >
                    {isUploading ? (
                      <svg
                        className="size-[24px] animate-spin text-[#0ea5e9]"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <CameraIcon className="size-[24px] shrink-0 text-[#0ea5e9]" />
                    )}
                    <span className="text-[14px] font-normal leading-5 text-[#0ea5e9]">
                      {isUploading
                        ? 'Загрузка...'
                        : 'Загрузите файл назначения'}
                    </span>
                  </div>
                  {prescriptionFiles.length > 0 && (
                    <div className="flex flex-col gap-[8px]">
                      {prescriptionFiles.map((file) => (
                        <UploadedFileItem
                          key={file.id}
                          fileName={file.fileName}
                          onDelete={() => {
                            setUploadError(null);
                            setPrescriptionFiles((prev) =>
                              prev.filter((f) => f.id !== file.id),
                            );
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {uploadError && (
                    <div className="text-red-500 mt-[12px] text-sm">
                      {uploadError}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              </>
            )}
            <DoctorAddMedicineToOrderForm
              onAdd={(medicine) =>
                setMedicineList((current) => current.concat([medicine]))
              }
            />
          </div>
          <div className="flex flex-col overflow-hidden rounded-r-lg bg-white text-brand-1">
            <div className="grow">
              <header className="rounded-tr-lg bg-brand-6 px-[24px] py-[16px]">
                <h2 className="text-[20px] font-bold leading-normal">Заказ</h2>
              </header>
              <div className="border-b-4 border-brand-3 px-[24px] pb-[24px] pt-[12px]">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-[52px]">&nbsp;</th>
                      <th className="text-left">Лекарство</th>
                      <th className="text-left">Частота приёма</th>
                      <th className="text-left">Цена</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicineList.length === 0 && (
                      <tr>
                        <td className="text-center" colSpan={4}>
                          Пусто
                        </td>
                      </tr>
                    )}
                    {medicineList.map(
                      (
                        {
                          totalCount,
                          count,
                          frequency,
                          medicine,
                          time,
                          duration,
                        },
                        index,
                      ) => (
                        <tr className="leading-[24px]" key={index}>
                          <td className="w-[52px]">
                            <button
                              className="inline-flex size-[32px] items-center justify-center rounded-md bg-brand-3"
                              onClick={() =>
                                setMedicineList((current) =>
                                  current.filter(
                                    (_, medicineIndex) =>
                                      medicineIndex !== index,
                                  ),
                                )
                              }
                              type="button"
                            >
                              <TrashIcon />
                            </button>
                          </td>
                          <td>
                            <strong className="block">{medicine.name}</strong>
                            <span className="text-brand-1/40">
                              x{totalCount}
                            </span>
                          </td>
                          <td>
                            <span className="block">
                              {totalCount} {formatMedicinePlural(totalCount)}
                              &nbsp;{frequency.value}
                            </span>
                            <span className="text-brand-1/40">
                              {time.map((t) => `${t.value}`).join(', ')}
                            </span>
                          </td>
                          <td>
                            {formatMoney(
                              medicine.medicine_price * count * duration,
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
                {checkMessages.length > 0 && (
                  <CheckMessagesBlock
                    messages={checkMessages}
                    className="mt-[16px]"
                  />
                )}
              </div>
              {medicineList.length > 0 && (
                <Controller
                  control={control}
                  name="description"
                  render={({ field, fieldState }) => (
                    <label className="block w-full p-[24px]">
                      <span className="mb-[12px] block">
                        Комментарий к заказу
                      </span>
                      <TextareaAutosize
                        {...field}
                        minRows={3}
                        maxRows={10}
                        className={classNames('input w-full resize-none', {
                          'input--invalid': fieldState.error,
                        })}
                        placeholder="Введите комментарий (необязательно)"
                      />
                    </label>
                  )}
                />
              )}
            </div>
            <footer className="flex justify-end border-t border-dashed border-brand-1/50 p-[24px]">
              <button
                className="button button--success px-[32px]"
                disabled={medicineList.length === 0 || isChecking || isCreating}
                onClick={() => {
                  if (isCheckSuccessful) {
                    createOrder();
                    setIsCheckSuccessful(false);
                  } else {
                    checkOrder();
                  }
                }}
                type="button"
              >
                {isChecking || isCreating ? (
                  <>Загрузка...</>
                ) : isCheckSuccessful ? (
                  'Подтвердить заказ'
                ) : (
                  'Проверить заказ'
                )}
              </button>
            </footer>
          </div>
        </div>
      </DoctorInnerPageLayout>
    </PrivateRoute>
  );
};
